import pLimit from "p-limit";
import { CHUNK_SIZE, DEFAULT_UPLOAD_CONCURRENCY, MAX_RETRY_ATTEMPTS } from "../shared/constants";
import {
  deriveChunkIv,
  formatRangeHeader,
  getChunkCount,
  getEncryptedRangeForPart,
  sortCompletedParts,
} from "../shared/chunks";
import {
  base64ToBytes,
  exportKeyToBase64,
  importKeyFromBase64,
  bytesToBase64Url,
  toArrayBuffer,
} from "../shared/base64";
import type {
  CompleteUploadResponse,
  CompletedPart,
  ContentType,
  DownloadStatusResponse,
  DownloadMetadataResponse,
  InitUploadResponse,
  PresignedUrlItem,
  PresignedUrlsResponse,
  UploadPolicyResponse,
} from "../shared/types";

interface UploadResult {
  shareUrl: string;
  fileKey: string;
}

interface UploadOptions {
  endpointBase?: string;
  concurrency?: number;
  maxReads?: number;
  expiresInHours?: number;
  contentType?: ContentType;
  onProgress?: (completedParts: number, totalParts: number) => void;
}

interface DownloadOptions {
  endpointBase?: string;
  onStatus?: (status: DownloadStatusResponse) => void;
  onProgress?: (completedParts: number, totalParts: number) => void;
}

function encodeFilenameForShareLink(filename: string): string {
  return bytesToBase64Url(new TextEncoder().encode(filename));
}

function decodeFilenameFromShareLink(value: string | null): string {
  if (!value) {
    return "download.bin";
  }

  try {
    return new TextDecoder().decode(base64ToBytes(value));
  } catch {
    return "download.bin";
  }
}

async function generateKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

function generateBaseIv(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(12));
}

function getLocationOrigin(): string {
  return globalThis.location?.origin ?? "http://localhost";
}

async function encryptChunk(chunkBlob: Blob, key: CryptoKey, baseIv: Uint8Array, partNumber: number): Promise<ArrayBuffer> {
  const plain = await chunkBlob.arrayBuffer();
  return crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(deriveChunkIv(baseIv, partNumber)) },
    key,
    plain,
  );
}

async function decryptChunk(cipher: ArrayBuffer, key: CryptoKey, baseIv: Uint8Array, partNumber: number): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(deriveChunkIv(baseIv, partNumber)) },
    key,
    cipher,
  );
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(await response.text() || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function retry<T>(task: () => Promise<T>, attempts = MAX_RETRY_ATTEMPTS): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt === attempts) {
        break;
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Retry failed.");
}

async function uploadEncryptedPart(
  chunkBlob: Blob,
  key: CryptoKey,
  baseIv: Uint8Array,
  partNumber: number,
  presignedUrl: string,
): Promise<CompletedPart> {
  return retry(async () => {
    const encryptedBuffer = await encryptChunk(chunkBlob, key, baseIv, partNumber);
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: encryptedBuffer,
    });

    if (!response.ok) {
      throw new Error(`Chunk ${partNumber} upload failed with status ${response.status}.`);
    }

    const ETag = response.headers.get("ETag");
    if (!ETag) {
      throw new Error(`Chunk ${partNumber} upload did not return ETag.`);
    }

    return { PartNumber: partNumber, ETag };
  });
}

export async function uploadFileE2EE(file: File, options: UploadOptions = {}): Promise<UploadResult> {
  const endpointBase = options.endpointBase ?? "";
  const concurrency = options.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY;
  const partsCount = getChunkCount(file.size);
  if (partsCount === 0) {
    throw new Error("Cannot upload an empty file.");
  }
  const filename = crypto.randomUUID();
  const key = await generateKey();
  const baseIv = generateBaseIv();
  const maxReads = options.maxReads ?? 1;
  const expiresInHours = options.expiresInHours ?? 24;
  const contentType = options.contentType ?? "file";

  const initResponse = await fetchJson<InitUploadResponse>(`${endpointBase}/api/upload/init`, {
    method: "POST",
    body: JSON.stringify({
      filename,
      partsCount,
      chunkSize: CHUNK_SIZE,
      maxReads,
      expiresInHours,
      contentType,
    }),
  });

  const urlsResponse = await fetchJson<PresignedUrlsResponse>(`${endpointBase}/api/upload/urls`, {
    method: "POST",
    body: JSON.stringify({ filename, uploadId: initResponse.uploadId, partsCount }),
  });

  const presignedMap = new Map<number, PresignedUrlItem>(urlsResponse.urls.map((item) => [item.partNumber, item]));
  const limit = pLimit(concurrency);
  let uploadedCount = 0;

  const completedParts = await Promise.all(
    Array.from({ length: partsCount }, (_, index) =>
      limit(async () => {
        const partNumber = index + 1;
        const chunkBlob = file.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE);
        const presigned = presignedMap.get(partNumber);
        if (!presigned) {
          throw new Error(`Missing presigned URL for part ${partNumber}.`);
        }
        const part = await uploadEncryptedPart(chunkBlob, key, baseIv, partNumber, presigned.url);
        uploadedCount += 1;
        options.onProgress?.(uploadedCount, partsCount);
        return part;
      }),
    ),
  );

  const sortedParts = sortCompletedParts(completedParts);
  await fetchJson<CompleteUploadResponse>(`${endpointBase}/api/upload/complete`, {
    method: "POST",
    body: JSON.stringify({ filename, uploadId: initResponse.uploadId, parts: sortedParts }),
  });

  const keyBase64 = await exportKeyToBase64(key);
  const ivBase64 = bytesToBase64Url(baseIv);
  const nameBase64 = encodeFilenameForShareLink(file.name);
  const typeSuffix = contentType === "text" ? "&type=text" : "";
  const shareUrl = `${getLocationOrigin()}/download/${filename}#key=${keyBase64}&iv=${ivBase64}&name=${nameBase64}${typeSuffix}`;
  return { shareUrl, fileKey: filename };
}

async function getDownloadMetadata(fileKey: string, endpointBase = ""): Promise<DownloadMetadataResponse> {
  const url = new URL(`${endpointBase}/api/download/metadata`, getLocationOrigin());
  url.searchParams.set("filename", fileKey);
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(await response.text() || "Failed to load download metadata.");
  }
  return response.json() as Promise<DownloadMetadataResponse>;
}

export async function getDownloadStatus(fileKey: string, endpointBase = ""): Promise<DownloadStatusResponse> {
  const url = new URL(`${endpointBase}/api/download/status`, getLocationOrigin());
  url.searchParams.set("filename", fileKey);
  const response = await fetch(url.toString());

  if (response.status === 404) {
    return {
      exists: false,
      remainingReads: 0,
      expiresAt: null,
      lastReadWarning: false,
    };
  }

  if (!response.ok) {
    throw new Error(await response.text() || "Failed to load download status.");
  }

  return response.json() as Promise<DownloadStatusResponse>;
}

export async function getUploadPolicy(endpointBase = ""): Promise<UploadPolicyResponse> {
  return fetchJson<UploadPolicyResponse>(`${endpointBase}/api/upload/policy`);
}

interface BinaryWriter {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

async function createWritableFile(originalName: string): Promise<BinaryWriter> {
  if ("showSaveFilePicker" in window) {
    const handle = await (window as Window & typeof globalThis & {
      showSaveFilePicker: (options: { suggestedName: string }) => Promise<FileSystemFileHandle>;
    }).showSaveFilePicker({ suggestedName: originalName });
    const writable = await handle.createWritable();
    return {
      async write(chunk) {
        await writable.write(toArrayBuffer(chunk));
      },
      async close() {
        await writable.close();
      },
    };
  }

  const stream = new TransformStream<Uint8Array, Uint8Array>();
  const chunks: ArrayBuffer[] = [];
  void stream.readable.pipeTo(
    new WritableStream<Uint8Array>({
      write(chunk) {
        chunks.push(toArrayBuffer(chunk));
      },
      close() {
        const blob = new Blob(chunks, { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = originalName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 0);
      },
    }),
  );
  const writer = stream.writable.getWriter();
  return {
    async write(chunk) {
      await writer.write(chunk);
    },
    async close() {
      await writer.close();
    },
  };
}

export async function downloadFileE2EE(fileKey: string, options: DownloadOptions = {}): Promise<void> {
  const endpointBase = options.endpointBase ?? "";
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  const keyValue = hash.get("key");
  const ivValue = hash.get("iv");
  const originalName = decodeFilenameFromShareLink(hash.get("name"));
  if (!keyValue || !ivValue) {
    throw new Error("Missing key or IV in URL fragment.");
  }

  const status = await getDownloadStatus(fileKey, endpointBase);
  options.onStatus?.(status);
  if (!status.exists) {
    throw new Error("File not found or has expired.");
  }

  const [key, baseIv, metadata] = await Promise.all([
    importKeyFromBase64(keyValue),
    Promise.resolve(base64ToBytes(ivValue)),
    getDownloadMetadata(fileKey, endpointBase),
  ]);

  const totalParts = Math.ceil(metadata.size / metadata.encryptedChunkSize);
  const writer = await createWritableFile(originalName);

  try {
    for (let index = 0; index < totalParts; index += 1) {
      const partNumber = index + 1;
      const { start, end } = getEncryptedRangeForPart(partNumber, metadata.size, metadata.chunkSize);
      const response = await fetch(metadata.downloadUrl, {
        headers: { Range: formatRangeHeader(start, end) },
      });

      if (!response.ok && response.status !== 206) {
        throw new Error(`Download failed for part ${partNumber}.`);
      }

      const encrypted = await response.arrayBuffer();
      const plain = await decryptChunk(encrypted, key, baseIv, partNumber);
      await writer.write(new Uint8Array(plain));
      options.onProgress?.(partNumber, totalParts);
    }
  } finally {
    await writer.close();
  }
}

export async function downloadTextE2EE(fileKey: string, options: DownloadOptions = {}): Promise<string> {
  const endpointBase = options.endpointBase ?? "";
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  const keyValue = hash.get("key");
  const ivValue = hash.get("iv");
  if (!keyValue || !ivValue) {
    throw new Error("Missing key or IV in URL fragment.");
  }

  const status = await getDownloadStatus(fileKey, endpointBase);
  options.onStatus?.(status);
  if (!status.exists) {
    throw new Error("Text not found or has expired.");
  }

  const [key, baseIv, metadata] = await Promise.all([
    importKeyFromBase64(keyValue),
    Promise.resolve(base64ToBytes(ivValue)),
    getDownloadMetadata(fileKey, endpointBase),
  ]);

  const totalParts = Math.ceil(metadata.size / metadata.encryptedChunkSize);
  const chunks: Uint8Array[] = [];

  for (let index = 0; index < totalParts; index += 1) {
    const partNumber = index + 1;
    const { start, end } = getEncryptedRangeForPart(partNumber, metadata.size, metadata.chunkSize);
    const response = await fetch(metadata.downloadUrl, {
      headers: { Range: formatRangeHeader(start, end) },
    });

    if (!response.ok && response.status !== 206) {
      throw new Error(`Download failed for part ${partNumber}.`);
    }

    const encrypted = await response.arrayBuffer();
    const plain = await decryptChunk(encrypted, key, baseIv, partNumber);
    chunks.push(new Uint8Array(plain));
    options.onProgress?.(partNumber, totalParts);
  }

  // Concatenate all chunks and decode as text
  const totalLength = chunks.reduce((sum, c) => sum + c.byteLength, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(combined);
}
